import { useCallback, useMemo } from "react";
import type { AskAboutDocument } from "../../buildDocumentReferences";
import { useRowChatDrag } from "../../chatDrag/useRowChatDrag";
import { useAddDocumentsToChat } from "../../useAddDocumentsToChat";
import type { DocumentRetrievalTableRow } from "./documentRetrievalViewConfig";

/**
 * `rowDragOut` for the Upload Statements tables: drags document rows into the
 * task chat. Month rows without a document can't be dragged.
 */
export function useDocumentRetrievalRowChatDrag<
    TDocument extends AskAboutDocument,
>({
    documents,
    selectedDocuments,
    clearSelection,
}: {
    documents: TDocument[];
    selectedDocuments: TDocument[];
    clearSelection: () => void;
}) {
    const { canAddToChat, addDocumentsToChat } = useAddDocumentsToChat();
    const documentsById = useMemo(
        () => new Map(documents.map((document) => [document.id, document])),
        [documents],
    );
    const getRowItem = useCallback(
        (row: DocumentRetrievalTableRow) =>
            row.documentId === null
                ? null
                : (documentsById.get(row.documentId) ?? null),
        [documentsById],
    );

    return useRowChatDrag({
        noun: "document",
        canAddToChat,
        getRowItem,
        selectedItems: selectedDocuments,
        addToChat: addDocumentsToChat,
        clearSelection,
    });
}
